import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Home', icon: 'bx-home' },
    { path: '/dashboard/portfolio', label: 'Portfolio', icon: 'bx-folder' },
    { path: '/dashboard/assessments', label: 'Assessments', icon: 'bx-bar-chart-alt-2' },
    { path: '/dashboard/global-leaderboard', label: 'Global Leaderboard', icon: 'bx-world' },
    { path: '/dashboard/college-leaderboard', label: 'College Leaderboard', icon: 'bx-buildings' },
    { path: '/dashboard/edit-profile', label: 'Edit Profile', icon: 'bx-edit-alt' },
    { path: '/dashboard/feedback', label: 'Feedback', icon: 'bx-message-square-detail', isNew: true },
  ];

  return (
    <div className="min-h-screen flex bg-gray-50 flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-white shadow-sm p-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">R</span>
          </div>
          <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
            RankMySkills
          </span>
        </div>
        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <i className={`bx ${isSidebarOpen ? 'bx-x' : 'bx-menu'} text-2xl`}></i>
        </button>
      </div>

      {/* Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <aside className={`
        w-64 bg-white shadow-lg fixed h-full z-50 transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:translate-x-0 top-0 left-0
      `}>
        <div className="p-6 border-b flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">R</span>
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
              RankMySkills
            </span>
          </div>
          <button 
            onClick={() => setIsSidebarOpen(false)}
            className="md:hidden text-gray-500 hover:text-gray-700"
          >
            <i className='bx bx-x text-2xl'></i>
          </button>
        </div>

        <nav className="p-4 pt-3 h-[calc(100vh-180px)] overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/dashboard'}
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `group flex items-center justify-between px-4 py-3 rounded-lg mb-2 transition-all text-sm ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 hover:shadow-sm'
                }`
              }
            >
              <div className="flex items-center space-x-3">
                <i className={`bx ${item.icon} text-xl`}></i>
                <span className="font-medium">{item.label}</span>
              </div>
              {item.isNew ? (
                <span className="relative overflow-hidden rounded-md border border-blue-300/90 bg-gradient-to-r from-blue-50 to-white px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.28em] text-blue-700 shadow-[0_0_0_1px_rgba(59,130,246,0.1),0_0_10px_rgba(59,130,246,0.26),0_0_18px_rgba(59,130,246,0.18)] animate-pulse">
                  <span className="relative z-10">NEW</span>
                  <span className="absolute inset-y-0 -left-10 w-6 rotate-12 bg-white/90 opacity-90 animate-[shimmer_2.4s_linear_infinite]"></span>
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t bg-white">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
              {user?.profile_photo ? (
                <img 
                  src={user.profile_photo.startsWith('http') ? user.profile_photo : `${process.env.REACT_APP_IMG_URL || 'http://localhost:5000'}${user.profile_photo}`} 
                  alt="Profile" 
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'inline';
                  }}
                />
              ) : null}
              <i className={`bx bx-user text-2xl text-gray-500 ${user?.profile_photo ? 'hidden' : ''}`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 p-4 md:p-8 w-full transition-all duration-300">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
